import { type SnakeCasedPropertiesDeep } from 'type-fest'

type ValidObjectKey = string | number | symbol

export function isPlainObject(testObj: Record<string, any>): boolean {
  return Object.prototype.toString.call(testObj).indexOf('Object') !== -1
}

type KeyTransformer = (input: string, key: string, value: any) => string
type ValueTransformer<T> = (input: T, key: string, value: T) => T

function transformKey(
  input: string,
  key: string,
  value: any,
  transformers: Array<KeyTransformer>
) {
  return transformers.reduce((out, fn) => fn(out, key, value), input)
}

function transformValue<V>(
  input: V,
  key: string,
  value: V,
  transformers: ValueTransformer<V>[]
) {
  return transformers.reduce((out, fn) => fn(out, key, value), input)
}

/**
 * Remove or transform desired keys and null|undefined values from an object; optionally recursive
 *
 **/
type CleanObjKeysAndValuesOptions = {
  keysToRemove?: string[]
  keyTransformers?: KeyTransformer[]
  valueTransformers?: ValueTransformer<any>[]
  recursive?: boolean
}

export function cleanObjKeysAndValues<T = Record<string, unknown>, U = T>(
  obj: T,
  options: CleanObjKeysAndValuesOptions = {}
): U {
  const {
    keysToRemove = [],
    keyTransformers = [(x) => x],
    valueTransformers = [(x) => x],
    recursive = false
  } = options
  return Object.fromEntries(
    Object.entries(obj ?? {})
      .map(([key, value]) => {
        if (keysToRemove.includes(key)) {
          return undefined
        }

        const nextVal = transformValue(value, key, value, valueTransformers)
        if (nextVal == null) {
          return undefined
        }

        return [
          transformKey(key, key, nextVal, keyTransformers),
          recursive && isPlainObject(nextVal)
            ? cleanObjKeysAndValues(nextVal, {
                keysToRemove,
                keyTransformers,
                valueTransformers,
                recursive
              })
            : nextVal
        ]
      })
      .filter(Boolean)
  )
}

export const hasKey = (obj: Record<string, any>, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(obj, key)

export const omit = <T = Record<string, any>>(
  object: Record<string, any> = {},
  paths: string | string[]
): T => {
  const omitKeys =
    typeof paths === 'string' ? new Set([paths]) : new Set((paths ?? []).flat())

  return Object.fromEntries(
    Object.entries(object).filter(([key]) => !omitKeys.has(key))
  ) as T
}

export const pick = <T, K extends keyof T>(
  object: T,
  ...keysToExtract: K[]
): Pick<T, K> => {
  const extractKeys: Array<K> = [...new Set(keysToExtract ?? [])]
  return Object.fromEntries(
    extractKeys.map((key) => [key, object[key]])
  ) as Pick<T, K>
}

export function toSnake(str: string): string {
  return str
    .replace(/([A-Z])/g, ($1: string) => {
      return `-${$1.toLowerCase()}`
    })
    .replace(/-/g, '_')
}

export function toCamel(str: string): string {
  return str.replace(/([_-][a-z])/gi, ($1: string) => {
    return $1.toUpperCase().replace('-', '').replace('_', '')
  })
}

export function isArray(
  input: Record<string, unknown> | Record<string, unknown>[] | unknown
): input is Record<string, unknown>[] {
  return Array.isArray(input)
}

export function isObject(
  obj: Record<string, unknown> | Record<string, unknown>[] | unknown
): obj is Record<string, unknown> {
  return obj === Object(obj) && !Array.isArray(obj) && typeof obj !== 'function'
}

export function keysToCamelCase<T>(input: T): T {
  return (function recurse<
    K extends Record<string, unknown> | Record<string, unknown>[] | unknown
  >(input: K): K {
    if (isObject(input)) {
      return Object.keys(input).reduce((acc, key) => {
        return Object.assign(acc, { [toCamel(key)]: recurse(input[key]) })
      }, {} as K)
    } else if (isArray(input)) {
      return input.map((i) => recurse(i)) as K
    }
    return input
  })(input)
}

export function keysToSnakeCase<T>(input: T): SnakeCasedPropertiesDeep<T> {
  return (function recurse<
    K extends Record<string, unknown> | Record<string, unknown>[] | unknown
  >(input: K): K {
    if (isObject(input)) {
      return Object.keys(input).reduce((acc, key) => {
        return Object.assign(acc, { [toSnake(key)]: recurse(input[key]) })
      }, {} as K)
    } else if (isArray(input)) {
      return input.map((i) => recurse(i)) as K
    }
    return input
  })(input)
}

/**
 * Create key map for O(1) object lookup instead of O(n) arr.find() lookup.
 *
 * For transformed values see `arrayToObject` and `arrayToObjectWithArrayValues`
 *
 * @example React usage with teams: Team[]
 * ```
 * const teamIdMap = useMemo(
 *     () => createLookupMapFromArray(teams, 'identifier'),
 *     [teams]
 *   )
 * ```
 */
export function createLookupMapFromArray<TData>(
  arr: Array<TData> | ReadonlyArray<TData> | undefined,
  getKey: string | ((item: TData) => string)
) {
  const newObj: Record<string, TData> = Object.create(null)

  return (arr ?? []).reduce((lookupMap, curItem) => {
    const mapKey =
      typeof getKey === 'string' ? curItem[getKey] : getKey(curItem)
    lookupMap[mapKey] = curItem
    return lookupMap
  }, newObj)
}

/** Similar to `createLookupMapFromArray` but values are transformed. */
export function arrayToObject<
  TData,
  TKey extends ValidObjectKey,
  TValue = TData
>({
  data,
  keyFn,
  valueFn
}: {
  data: TData[] | ReadonlyArray<TData> | undefined
  keyFn: (item: TData) => TKey
  valueFn: (item: TData) => TValue
}) {
  const newObj: Record<TKey, TValue> = Object.create(null)

  return (data ?? []).reduce((acc, item) => {
    const itemKey = keyFn(item)
    const value = valueFn(item)

    acc[itemKey] = value

    return acc
  }, newObj)
}

/** Similar to `createLookupMapFromArray` and `arrayToObject` but values are
 * transformed and collected in an array. */
export function arrayToObjectWithArrayValues<
  TData,
  TKey extends ValidObjectKey,
  TValue = TData
>({
  data,
  keyFn,
  valueFn
}: {
  data: TData[] | ReadonlyArray<TData> | undefined
  keyFn: (item: TData) => TKey
  valueFn: (item: TData) => TValue
}) {
  const newObj: Record<TKey, TValue[]> = Object.create(null)

  return (data ?? []).reduce((acc, item) => {
    const itemKey = keyFn(item)
    const value = valueFn(item)

    acc[itemKey] ??= []
    acc[itemKey].push(value)

    return acc
  }, newObj)
}

/**
 * Get value from an object by key path
 * @param obj
 * @param path
 * @param defaultValue
 */
export function get<T = Record<string, unknown>>(
  obj: T,
  path: string,
  defaultValue = undefined
) {
  const travel = (regexp: RegExp) =>
    String.prototype.split
      .call(path, regexp)
      .filter(Boolean)
      .reduce(
        (res: { [x: string]: unknown }, key: string | number) =>
          res !== null && res !== undefined ? res[key] : res,
        obj
      )
  const result = travel(/[,[\]]+?/) || travel(/[,[\].]+?/)
  return result === undefined || result === obj ? defaultValue : result
}
